'use client';

import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CheckCircle2, 
  Plus, 
  Trash2, 
  Clock, 
  Upload, 
  FileImage,
  Loader2,
  Calendar as CalendarIcon,
  GripVertical,
  Download,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { SchoolViewExtra } from '@/types/api';
import { useSchoolSchedules, useSchoolYears } from '@/hooks/use-schools';
import { schoolsService } from '@/services/schools.service';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface SetupStep3Props {
  school: SchoolViewExtra;
  schoolId: string;
  onComplete: () => void;
}

export interface Step3Ref {
  save: () => Promise<boolean>;
  canSave: () => boolean;
}

interface EditablePeriod {
  name: string;
  startTime: string;
  endTime: string;
  isCustom?: boolean;
  dayType?: { id: string; name: string };
  periodType?: { id: string; name: string };
}

interface EditableLunch {
  name: string;
  startTime: string;
  endTime: string;
}

interface EditableSchedule {
  dayTypeId: string;
  dayTypeName: string;
  displayName: string;
  periods: EditablePeriod[];
  lunches: EditableLunch[];
  imageUrl?: string;
  genId?: string;
}

export const SetupStep3Schedules = forwardRef<Step3Ref, SetupStep3Props>(
  function SetupStep3Schedules({ school, schoolId, onComplete }, ref) {
    const { data: schedules, isLoading } = useSchoolSchedules(schoolId);
    const { data: schoolYears } = useSchoolYears(schoolId);
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [editableSchedules, setEditableSchedules] = useState<EditableSchedule[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const schoolPrimaryColor = school?.primaryColor || '#1e40af';
    
    const getContrastColor = (hexColor: string): string => {
      const hex = hexColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? 'black' : 'white';
    };

    const calculateDuration = (start: string, end: string): number => {
      if (!start || !end) return 0;
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      return (eh * 60 + em) - (sh * 60 + sm);
    };

    const parseTimeToHHMM = (time: string): string => {
      if (!time) return '';
      
      // If already in HH:mm format, return as is
      if (/^\d{2}:\d{2}$/.test(time)) {
        return time;
      }
      
      // Parse 12-hour format (e.g., "8:30 AM", "12:00 PM")
      const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = match[2];
        const period = match[3]?.toUpperCase();
        
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return `${String(hours).padStart(2, '0')}:${minutes}`;
      }
      
      return time;
    };

    // Check if we have valid editable schedules ready to save
    const hasValidData = editableSchedules.some(s => s.periods.length > 0 && s.dayTypeId);

    // Check if step is actually saved to backend
    const isStepSaved = !isLoading && schedules && schedules.length > 0;

    useEffect(() => {
      if (isStepSaved) {
        onComplete();
      }
    }, [isStepSaved, onComplete]);

    const hasPrereqs = school.dayTypes && school.dayTypes.length > 0 &&
      school.rotationPattern && school.rotationPattern.length > 0 &&
      schoolYears && schoolYears.length > 0;

    const processImageFile = async (file: File) => {
      setIsUploading(true);
      setIsDragOver(false);
      
      try {
        toast.info('Analyzing schedule image...');
        
        const results = await schoolsService.uploadAndParseScheduleImage(file);

        if (results.length === 0) {
          toast.error('No schedules could be parsed from the image. Please try a clearer image.');
          return;
        }

        const newSchedules: EditableSchedule[] = results.map((parsed, index) => {
          const canConfigurePeriodTypes = school?.periodTypes && school.periodTypes.length > 0;
          
          // Helper function to find matching day type from schedule title
          const findMatchingDayType = () => {
            if (!school?.dayTypes) return null;
            
            const titleNormalized = parsed.title.toLowerCase().trim();
            
            // Try to find a day type that matches the schedule title
            // Check if title contains the day type name (e.g., "A Day" contains "A")
            for (const dt of school.dayTypes) {
              const dayTypeNormalized = dt.name.toLowerCase();
              
              // Exact match
              if (titleNormalized === dayTypeNormalized) {
                return dt;
              }
              
              // Title contains day type (e.g., "A Day" contains "A")
              // Use word boundaries to avoid partial matches
              const regex = new RegExp(`\\b${dayTypeNormalized}\\b`, 'i');
              if (regex.test(parsed.title)) {
                return dt;
              }
            }
            
            // Fall back to cycling through day types by index if no match found
            return school.dayTypes[index % (school.dayTypes.length || 1)] || null;
          };
          
          const dayType = findMatchingDayType();
          
          // Helper function to match period names more flexibly
          const findMatchingPeriodType = (periodName: string) => {
            if (!school?.periodTypes) return null;
            
            const normalizedName = periodName.toLowerCase().trim();
            
            // Try exact match first
            let match = school.periodTypes.find(
              pt => pt.name.toLowerCase() === normalizedName
            );
            if (match) return match;
            
            // Try matching just the number/letter (e.g., "1" matches "Period 1" or "1st Period")
            for (const pt of school.periodTypes) {
              const ptNormalized = pt.name.toLowerCase();
              // Extract number or letter from period type name
              const ptNumber = ptNormalized.match(/\d+|[a-z](?![a-z])/);
              const periodNumber = normalizedName.match(/\d+|[a-z](?![a-z])/);
              
              if (ptNumber && periodNumber && ptNumber[0] === periodNumber[0]) {
                return pt;
              }
            }
            
            return null;
          };
          
          return {
            dayTypeId: dayType?.id || '',
            dayTypeName: dayType?.name || parsed.title,
            displayName: dayType?.name || parsed.title,
            periods: parsed.periods.map((p) => {
              const matchingPeriodType = findMatchingPeriodType(p.name);
              
              // If we have period types configured, a matching period type, and a day type,
              // assign them to the period
              if (canConfigurePeriodTypes && dayType && matchingPeriodType) {
                return {
                  name: matchingPeriodType.name,
                  startTime: parseTimeToHHMM(p.startTime),
                  endTime: parseTimeToHHMM(p.endTime),
                  isCustom: false,
                  dayType: { id: dayType.id, name: dayType.name },
                  periodType: { id: matchingPeriodType.id, name: matchingPeriodType.name },
                };
              }
              
              // For non-matching periods, mark as custom
              return {
                name: p.name,
                startTime: parseTimeToHHMM(p.startTime),
                endTime: parseTimeToHHMM(p.endTime),
                isCustom: true,
              };
            }),
            lunches: parsed.lunches.map(l => ({
              name: l.name,
              startTime: parseTimeToHHMM(l.startTime),
              endTime: parseTimeToHHMM(l.endTime),
            })),
            imageUrl: parsed.url,
            genId: parsed.genId,
          };
        });

        setEditableSchedules(newSchedules);
        setShowManualEntry(false);
        toast.success(`Found ${results.length} schedule${results.length > 1 ? 's' : ''}. Edit below and save.`);
      } catch (error) {
        console.error('Failed to parse schedule image:', error);
        toast.error('Failed to parse schedule image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await processImageFile(file);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      
      if (isUploading) return;

      const files = event.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        
        // Check if it's an image file
        if (!file.type.startsWith('image/')) {
          toast.error('Please upload an image file (JPG, PNG, HEIC)');
          setIsDragOver(false);
          return;
        }

        await processImageFile(file);
      }
    };

    const initManualSchedules = () => {
      if (!school.dayTypes) return;
      
      let sortedDayTypes = [...school.dayTypes];
      if (school.rotationPattern && school.rotationPattern.length > 0) {
        sortedDayTypes = sortedDayTypes.sort((a, b) => {
          const aIndex = school.rotationPattern!.indexOf(a.name);
          const bIndex = school.rotationPattern!.indexOf(b.name);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      }
      
      setEditableSchedules(sortedDayTypes.map(dt => ({
        dayTypeId: dt.id,
        dayTypeName: dt.name,
        displayName: dt.name,
        periods: [],
        lunches: [],
      })));
      setShowManualEntry(true);
    };

    const updateScheduleDayType = (index: number, dayTypeId: string) => {
      const dayType = school.dayTypes?.find(dt => dt.id === dayTypeId);
      if (!dayType) return;
      
      setEditableSchedules(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          dayTypeId,
          dayTypeName: dayType.name,
          displayName: dayType.name,
        };
        return updated;
      });
    };

    const updatePeriod = (scheduleIndex: number, periodIndex: number, updates: Partial<EditablePeriod>) => {
      setEditableSchedules(prev => 
        prev.map((s, i) => 
          i === scheduleIndex 
            ? { 
                ...s, 
                periods: s.periods.map((p, pi) => 
                  pi === periodIndex ? { ...p, ...updates } : p
                )
              }
            : s
        )
      );
    };

    const addMinutesToTime = (time: string, minutes: number): string => {
      const [h, m] = time.split(':').map(Number);
      const totalMinutes = h * 60 + m + minutes;
      const newHours = Math.floor(totalMinutes / 60) % 24;
      const newMinutes = totalMinutes % 60;
      return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    };

    const addPeriod = (scheduleIndex: number) => {
      setEditableSchedules(prev => {
        const schedule = prev[scheduleIndex];
        const periods = schedule.periods;
        
        let startTime = '08:00';
        let endTime = '08:45';
        
        if (periods.length > 0) {
          const lastPeriod = periods[periods.length - 1];
          startTime = addMinutesToTime(lastPeriod.endTime, 5);
          endTime = addMinutesToTime(startTime, 45);
        }

        const canConfigurePeriodTypes = school?.periodTypes && school.periodTypes.length > 0;
        const periodTypeIndex = periods.length % (school?.periodTypes?.length || 1);
        const periodType = school?.periodTypes?.[periodTypeIndex];
        const dayType = school?.dayTypes?.find(dt => dt.id === schedule.dayTypeId);
        
        const newPeriod: EditablePeriod = {
          name: periodType?.name || `Period ${periods.length + 1}`,
          startTime,
          endTime,
          isCustom: !canConfigurePeriodTypes,
        };

        if (canConfigurePeriodTypes && dayType && periodType) {
          newPeriod.dayType = { id: dayType.id, name: dayType.name };
          newPeriod.periodType = { id: periodType.id, name: periodType.name };
          newPeriod.isCustom = false;
        }
        
        return prev.map((s, i) => 
          i === scheduleIndex 
            ? { ...s, periods: [...s.periods, newPeriod] }
            : s
        );
      });
    };

    const deletePeriod = (scheduleIndex: number, periodIndex: number) => {
      setEditableSchedules(prev => 
        prev.map((s, i) => 
          i === scheduleIndex 
            ? { ...s, periods: s.periods.filter((_, pi) => pi !== periodIndex) }
            : s
        )
      );
    };

    const updateLunch = (scheduleIndex: number, lunchIndex: number, field: keyof EditableLunch, value: string) => {
      setEditableSchedules(prev => 
        prev.map((s, i) => 
          i === scheduleIndex 
            ? { 
                ...s, 
                lunches: s.lunches.map((l, li) => 
                  li === lunchIndex ? { ...l, [field]: value } : l
                )
              }
            : s
        )
      );
    };

    const deleteLunch = (scheduleIndex: number, lunchIndex: number) => {
      setEditableSchedules(prev => 
        prev.map((s, i) => 
          i === scheduleIndex 
            ? { ...s, lunches: s.lunches.filter((_, li) => li !== lunchIndex) }
            : s
        )
      );
    };

    const addLunch = (scheduleIndex: number) => {
      setEditableSchedules(prev => {
        const schedule = prev[scheduleIndex];
        const lunches = schedule.lunches;
        
        let startTime = '12:00';
        let endTime = '12:30';
        
        if (lunches.length > 0) {
          const lastLunch = lunches[lunches.length - 1];
          startTime = lastLunch.endTime;
          const [h, m] = startTime.split(':').map(Number);
          const totalMinutes = h * 60 + m + 30;
          endTime = `${String(Math.floor(totalMinutes / 60) % 24).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
        }
        
        const newLunch = {
          name: String.fromCharCode(65 + lunches.length), // A, B, C...
          startTime,
          endTime,
        };
        
        return prev.map((s, i) => 
          i === scheduleIndex 
            ? { ...s, lunches: [...s.lunches, newLunch] }
            : s
        );
      });
    };

    const copySchedule = (fromIndex: number, toIndex: number) => {
      setEditableSchedules(prev => 
        prev.map((s, i) => 
          i === toIndex 
            ? { 
                ...s, 
                periods: prev[fromIndex].periods.map(p => ({ ...p })),
                lunches: prev[fromIndex].lunches.map(l => ({ ...l })),
              }
            : s
        )
      );
      toast.success(`Copied from ${editableSchedules[fromIndex].dayTypeName}`);
    };

    const deleteSchedule = (index: number) => {
      setEditableSchedules(prev => prev.filter((_, i) => i !== index));
    };

    const addSchedule = () => {
      const usedDayTypeIds = new Set(editableSchedules.map(s => s.dayTypeId));
      const availableDayType = school.dayTypes?.find(dt => !usedDayTypeIds.has(dt.id)) || school.dayTypes?.[0];
      
      if (!availableDayType) return;
      
      setEditableSchedules(prev => [...prev, {
        dayTypeId: availableDayType.id,
        dayTypeName: availableDayType.name,
        displayName: availableDayType.name,
        periods: [],
        lunches: [],
      }]);
    };

    // Drag and drop handlers
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handlePeriodDragEnd = (scheduleIndex: number) => (event: DragEndEvent) => {
      const { active, over } = event;
      
      if (over && active.id !== over.id) {
        setEditableSchedules(prev => 
          prev.map((schedule, idx) => {
            if (idx !== scheduleIndex) return schedule;
            
            const oldIndex = schedule.periods.findIndex((_, i) => `period-${scheduleIndex}-${i}` === active.id);
            const newIndex = schedule.periods.findIndex((_, i) => `period-${scheduleIndex}-${i}` === over.id);
            
            return {
              ...schedule,
              periods: arrayMove(schedule.periods, oldIndex, newIndex),
            };
          })
        );
      }
    };

    const handleLunchDragEnd = (scheduleIndex: number) => (event: DragEndEvent) => {
      const { active, over } = event;
      
      if (over && active.id !== over.id) {
        setEditableSchedules(prev => 
          prev.map((schedule, idx) => {
            if (idx !== scheduleIndex) return schedule;
            
            const oldIndex = schedule.lunches.findIndex((_, i) => `lunch-${scheduleIndex}-${i}` === active.id);
            const newIndex = schedule.lunches.findIndex((_, i) => `lunch-${scheduleIndex}-${i}` === over.id);
            
            return {
              ...schedule,
              lunches: arrayMove(schedule.lunches, oldIndex, newIndex),
            };
          })
        );
      }
    };

    const handleScheduleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      
      if (over && active.id !== over.id) {
        setEditableSchedules(prev => {
          const oldIndex = prev.findIndex((_, i) => `schedule-${i}` === active.id);
          const newIndex = prev.findIndex((_, i) => `schedule-${i}` === over.id);
          
          return arrayMove(prev, oldIndex, newIndex);
        });
      }
    };

    // Sortable components
    const SortablePeriod = ({ 
      id, 
      period, 
      periodIndex, 
      scheduleIndex, 
      duration, 
      canConfigurePeriodTypes,
      schoolPrimaryColor,
    }: { 
      id: string; 
      period: EditablePeriod; 
      periodIndex: number; 
      scheduleIndex: number; 
      duration: number;
      canConfigurePeriodTypes: boolean;
      schoolPrimaryColor: string;
    }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({ id });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      };

      return (
        <div
          ref={setNodeRef}
          style={style}
          className="group flex items-center gap-3 py-3 px-4 rounded-lg border-2 bg-card transition-all hover:shadow-md hover:-translate-y-0.5"
          {...attributes}
        >
          <button
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors p-1 -ml-2"
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          
          {canConfigurePeriodTypes ? (
            <>
              {period.isCustom ? (
                <>
                  <Select
                    value="custom"
                    onValueChange={(value) => {
                      if (value !== 'custom') {
                        const dayType = school.dayTypes?.find(dt => dt.id === value);
                        const periodType = school.periodTypes?.[0];
                        if (dayType && periodType) {
                          updatePeriod(scheduleIndex, periodIndex, {
                            isCustom: false,
                            name: periodType.name,
                            dayType: { id: dayType.id, name: dayType.name },
                            periodType: { id: periodType.id, name: periodType.name },
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                      {school.dayTypes?.map((dt) => (
                        <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Period name"
                    value={period.name}
                    onChange={(e) => updatePeriod(scheduleIndex, periodIndex, { name: e.target.value })}
                    className="h-9 w-[140px] font-medium"
                    autoFocus={!period.name}
                  />
                </>
              ) : (
                <>
                  <Select
                    value={period.dayType?.id || ''}
                    onValueChange={(value) => {
                      if (value === 'custom') {
                        const currentName = period.periodType?.name || period.name || `Period ${periodIndex + 1}`;
                        updatePeriod(scheduleIndex, periodIndex, {
                          isCustom: true,
                          name: currentName,
                          dayType: undefined,
                          periodType: undefined,
                        });
                      } else {
                        const dayType = school.dayTypes?.find(dt => dt.id === value);
                        if (dayType) {
                          updatePeriod(scheduleIndex, periodIndex, {
                            dayType: { id: dayType.id, name: dayType.name },
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 w-[100px]">
                      <SelectValue placeholder="Day" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                      {school.dayTypes?.map((dt) => (
                        <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={period.periodType?.id || ''}
                    onValueChange={(value) => {
                      const periodType = school.periodTypes?.find(pt => pt.id === value);
                      if (periodType) {
                        updatePeriod(scheduleIndex, periodIndex, {
                          name: periodType.name,
                          periodType: { id: periodType.id, name: periodType.name },
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="h-9 w-[100px]">
                      <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                      {school.periodTypes?.map((pt) => (
                        <SelectItem key={pt.id} value={pt.id}>{pt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </>
          ) : (
            <Input
              placeholder="Period name"
              value={period.name}
              onChange={(e) => updatePeriod(scheduleIndex, periodIndex, { name: e.target.value })}
              className="h-9 w-[140px] font-medium"
            />
          )}
          
          <div className="flex items-center gap-2">
            <Input
              type="time"
              value={period.startTime}
              onChange={(e) => updatePeriod(scheduleIndex, periodIndex, { startTime: e.target.value })}
              className="h-9 w-[110px] font-mono text-sm [&::-webkit-calendar-picker-indicator]:hidden"
            />
            <span className="text-sm text-muted-foreground font-bold">→</span>
            <Input
              type="time"
              value={period.endTime}
              onChange={(e) => updatePeriod(scheduleIndex, periodIndex, { endTime: e.target.value })}
              className="h-9 w-[110px] font-mono text-sm [&::-webkit-calendar-picker-indicator]:hidden"
            />
          </div>
          <Badge 
            variant="secondary" 
            className="font-mono text-sm h-7 px-3 shrink-0"
            style={{ 
              backgroundColor: `${schoolPrimaryColor}15`,
              color: schoolPrimaryColor,
              borderColor: `${schoolPrimaryColor}30`,
              borderWidth: '1px'
            }}
          >
            {duration}m
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => deletePeriod(scheduleIndex, periodIndex)}
            className="h-9 w-9 ml-auto opacity-0 group-hover:opacity-100 transition-all hover:scale-110 hover:text-destructive hover:bg-destructive/15"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      );
    };

    const SortableLunch = ({ 
      id, 
      lunch, 
      lunchIndex, 
      scheduleIndex, 
      duration,
      schoolPrimaryColor,
    }: { 
      id: string; 
      lunch: EditableLunch; 
      lunchIndex: number; 
      scheduleIndex: number; 
      duration: number;
      schoolPrimaryColor: string;
    }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({ id });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      };

      return (
        <div
          ref={setNodeRef}
          style={style}
          className="group flex items-center gap-3 py-3 px-4 rounded-lg border-2 bg-card transition-all hover:shadow-md hover:-translate-y-0.5"
        >
          <button
            className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors p-1 -ml-2"
            {...listeners}
            {...attributes}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          
          <div className="text-2xl shrink-0">🍔</div>
          <Input
            value={lunch.name}
            onChange={(e) => updateLunch(scheduleIndex, lunchIndex, 'name', e.target.value)}
            className="h-9 w-20 font-medium"
          />
          <div className="flex items-center gap-2">
            <Input
              type="time"
              value={lunch.startTime}
              onChange={(e) => updateLunch(scheduleIndex, lunchIndex, 'startTime', e.target.value)}
              className="h-9 w-[110px] font-mono text-sm [&::-webkit-calendar-picker-indicator]:hidden"
            />
            <span className="text-sm text-muted-foreground font-bold">→</span>
            <Input
              type="time"
              value={lunch.endTime}
              onChange={(e) => updateLunch(scheduleIndex, lunchIndex, 'endTime', e.target.value)}
              className="h-9 w-[110px] font-mono text-sm [&::-webkit-calendar-picker-indicator]:hidden"
            />
          </div>
          <Badge 
            variant="secondary" 
            className="font-mono text-sm h-7 px-3 shrink-0"
            style={{ 
              backgroundColor: `${schoolPrimaryColor}15`,
              color: schoolPrimaryColor,
              borderColor: `${schoolPrimaryColor}30`,
              borderWidth: '1px'
            }}
          >
            {duration}m
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => deleteLunch(scheduleIndex, lunchIndex)}
            className="h-9 w-9 ml-auto opacity-0 group-hover:opacity-100 transition-all hover:scale-110 hover:text-destructive hover:bg-destructive/15"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      );
    };

    const SortableSchedule = ({ 
      id, 
      schedule, 
      scheduleIndex,
      dragHandleProps,
      children,
    }: { 
      id: string; 
      schedule: EditableSchedule; 
      scheduleIndex: number;
      dragHandleProps?: any;
      children: React.ReactNode;
    }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({ id });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      };

      return (
        <Card 
          ref={setNodeRef} 
          style={{...style, borderColor: `${schoolPrimaryColor}20`}} 
          className="border-2" 
        >
          <div className="flex items-start">
            <button
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors p-3 pt-5"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-5 w-5" />
            </button>
            <div className="flex-1">
              {children}
            </div>
          </div>
        </Card>
      );
    };

    useImperativeHandle(ref, () => ({
      canSave: () => {
        // Can save if there are existing schedules OR we have editable schedules with periods
        if (isStepSaved) return true;
        return editableSchedules.some(s => s.periods.length > 0 && s.dayTypeId);
      },
      save: async () => {
        // If already complete, nothing to save
        if (isStepSaved) return true;

        const validSchedules = editableSchedules.filter(s => s.periods.length > 0 && s.dayTypeId);
        if (validSchedules.length === 0) {
          toast.error('Please add at least one schedule with periods');
          return false;
        }

        try {
          const promises = validSchedules.map(async (scheduleData) => {
            // Sort periods by start time before saving
            const sortedPeriods = [...scheduleData.periods].sort((a, b) => {
              const aTime = calculateDuration('00:00', a.startTime || '00:00');
              const bTime = calculateDuration('00:00', b.startTime || '00:00');
              return aTime - bTime;
            });
            
            const schedule = {
              schoolId,
              genId: scheduleData.genId,
              displayName: scheduleData.displayName,
              variant: 'core' as const,
              dayType: {
                id: scheduleData.dayTypeId,
                name: scheduleData.dayTypeName,
              },
              isInLsc: true,
              isInAcf: true,
              isDraft: false,
              isUserDraft: false,
              periods: sortedPeriods.map(p => {
                const basePeriod = {
                  id: '',
                  name: p.name,
                  startTime: p.startTime,
                  endTime: p.endTime,
                };
                if (p.dayType && p.periodType) {
                  return { ...basePeriod, dayType: p.dayType, periodType: p.periodType };
                }
                return basePeriod;
              }),
              lunchWaves: scheduleData.lunches
                .filter((_, idx) => {
                  // Only include lunch waves if we have valid slots for them
                  if (school.lunchSettings?.type === 'waves' && school.lunchSettings.slots) {
                    return idx < school.lunchSettings.slots.length;
                  }
                  return false; // Skip lunch waves if school doesn't have wave lunch configured
                })
                .map((l, idx) => {
                  const slots = school.lunchSettings?.type === 'waves' ? school.lunchSettings.slots : [];
                  return {
                    id: '',
                    name: l.name,
                    startTime: l.startTime,
                    endTime: l.endTime,
                    slot: slots[idx],
                  };
                }),
              image: scheduleData.imageUrl ? {
                source: 'ai' as const,
                url: scheduleData.imageUrl,
              } : undefined,
            };

            return schoolsService.createSchedule(schoolId, schedule, school.timezone);
          });

          await Promise.all(promises);
          
          toast.success(`Created ${validSchedules.length} schedule${validSchedules.length > 1 ? 's' : ''}! 🎉`);
          setEditableSchedules([]);
          await queryClient.invalidateQueries({ queryKey: ['school', schoolId, 'schedules'] });
          return true;
        } catch (error) {
          console.error('Step 3 save error:', error);
          toast.error('Failed to create schedules. Please try again.');
          return false;
        }
      }
    }));

    return (
      <div className="space-y-6">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />

        {!hasPrereqs && (
          <div className="p-8 rounded-lg border-2 border-dashed text-center" style={{ borderColor: `${schoolPrimaryColor}20` }}>
            <p className="text-muted-foreground">Complete Steps 1 & 2 first</p>
          </div>
        )}

        {!isLoading && isStepSaved && (
          <>
            <Card className="border-2" style={{ borderColor: `${schoolPrimaryColor}20`, backgroundColor: `${schoolPrimaryColor}05` }}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" style={{ color: schoolPrimaryColor }} />
                  <CardTitle className="text-lg">Schedules Created</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {schedules.map((schedule) => {
                    const totalMinutes = schedule.periods.reduce((sum, period) => {
                      return sum + calculateDuration(period.startTime, period.endTime);
                    }, 0);
                    const hours = Math.floor(totalMinutes / 60);
                    const minutes = totalMinutes % 60;

                    return (
                      <div
                        key={schedule.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-card border-2"
                        style={{ borderColor: `${schoolPrimaryColor}20` }}
                      >
                        <div>
                          <p className="font-semibold">{schedule.displayName}</p>
                          <p className="text-sm text-muted-foreground">
                            {schedule.periods.length} periods • {hours}h {minutes}m
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            <Alert>
              <CalendarIcon className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium mb-1">Next Step: Build Your Calendar</p>
                <p className="text-sm">
                  Now that your schedules are created, you can build your school calendar. 
                  Go to the <strong>Overview</strong> tab and click <strong>Build Calendar</strong> to assign schedules to specific dates.
                </p>
              </AlertDescription>
            </Alert>
          </>
        )}

        {hasPrereqs && !isStepSaved && editableSchedules.length === 0 && (
          <div className="space-y-6">
            <Card 
              className="border-2 overflow-hidden transition-all hover:shadow-lg cursor-pointer group"
              style={{ 
                borderColor: isDragOver ? schoolPrimaryColor : `${schoolPrimaryColor}80`,
                borderWidth: isDragOver ? '3px' : '2px',
                borderStyle: isDragOver ? 'solid' : 'solid'
              }}
              onClick={() => !isUploading && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div 
                className="p-8 text-center transition-all"
                style={{ 
                  background: isDragOver 
                    ? `linear-gradient(135deg, ${schoolPrimaryColor}20 0%, ${schoolPrimaryColor}30 100%)`
                    : `linear-gradient(135deg, ${schoolPrimaryColor}08 0%, ${schoolPrimaryColor}15 100%)`
                }}
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center transition-all group-hover:scale-110"
                  style={{ 
                    backgroundColor: isDragOver ? `${schoolPrimaryColor}40` : `${schoolPrimaryColor}20`,
                    transform: isDragOver ? 'scale(1.1)' : 'scale(1)'
                  }}
                >
                  {isUploading ? (
                    <Loader2 className="h-8 w-8 animate-spin" style={{ color: schoolPrimaryColor }} />
                  ) : isDragOver ? (
                    <Download className="h-8 w-8 animate-bounce" style={{ color: schoolPrimaryColor }} />
                  ) : (
                    <Upload className="h-8 w-8" style={{ color: schoolPrimaryColor }} />
                  )}
                </div>
                
                <h3 className="text-xl font-bold mb-2">
                  {isUploading ? 'Analyzing schedule...' : isDragOver ? 'Drop to Upload' : 'Upload Schedule Image'}
                </h3>
                <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                  {isUploading 
                    ? 'This usually takes about 20 seconds.'
                    : isDragOver
                    ? 'Release to upload your schedule image'
                    : 'Drag & drop an image here, or click to browse'}
                </p>
                
                {!isUploading && (
                  <Button
                    size="lg"
                    className="mt-2 text-base font-semibold px-8"
                    style={{ 
                      backgroundColor: schoolPrimaryColor,
                      color: getContrastColor(schoolPrimaryColor)
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    <Upload className="mr-2 h-5 w-5" />
                    Choose Image
                  </Button>
                )}
                
                <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    ~20 seconds
                  </div>
                  <div className="flex items-center gap-1">
                    <FileImage className="h-4 w-4" />
                    JPG, PNG, HEIC
                  </div>
                </div>
              </div>
            </Card>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-3">or</p>
              <Button
                variant="outline"
                onClick={initManualSchedules}
                className="border-dashed"
              >
                <Plus className="mr-2 h-4 w-4" />
                Enter schedule manually
              </Button>
            </div>
          </div>
        )}

        {editableSchedules.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${schoolPrimaryColor}15` }}
                >
                  <Clock className="h-5 w-5" style={{ color: schoolPrimaryColor }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {editableSchedules.length} Schedule{editableSchedules.length > 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Edit periods and times, then click Save & Continue • Drag <GripVertical className="inline h-3 w-3" /> to reorder
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditableSchedules([]);
                  setShowManualEntry(false);
                }}
              >
                Start Over
              </Button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleScheduleDragEnd}
            >
              <SortableContext
                items={editableSchedules.map((_, i) => `schedule-${i}`)}
                strategy={verticalListSortingStrategy}
              >
                {editableSchedules.map((schedule, scheduleIndex) => (
                  <SortableSchedule
                    key={`schedule-${scheduleIndex}`}
                    id={`schedule-${scheduleIndex}`}
                    schedule={schedule}
                    scheduleIndex={scheduleIndex}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                      {schedule.imageUrl && (
                        <div className="w-12 h-12 rounded-lg overflow-hidden border bg-muted">
                          <img src={schedule.imageUrl} alt="Schedule" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-medium">Day Type:</Label>
                        <Select
                          value={schedule.dayTypeId}
                          onValueChange={(value) => updateScheduleDayType(scheduleIndex, value)}
                        >
                          <SelectTrigger className="w-[140px] h-9">
                            <SelectValue placeholder="Select day type" />
                          </SelectTrigger>
                          <SelectContent>
                            {school.dayTypes?.map((dt) => (
                              <SelectItem key={dt.id} value={dt.id}>
                                {dt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {scheduleIndex > 0 && editableSchedules[scheduleIndex - 1].periods.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copySchedule(scheduleIndex - 1, scheduleIndex)}
                          className="h-8 text-xs"
                        >
                          Copy from above
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteSchedule(scheduleIndex)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Periods</Label>
                      <Button
                        size="sm"
                        onClick={() => addPeriod(scheduleIndex)}
                        className="h-8 text-xs group transition-all hover:shadow-md"
                        style={{ 
                          backgroundColor: schoolPrimaryColor,
                          color: getContrastColor(schoolPrimaryColor)
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1 transition-transform group-hover:rotate-90" />
                        Add Period
                      </Button>
                    </div>
                    
                    {schedule.periods.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                        <Clock className="h-6 w-6 mx-auto mb-2 opacity-50" />
                        <p className="text-sm font-medium">No periods yet</p>
                        <p className="text-xs mt-1">Click "Add Period" to get started</p>
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handlePeriodDragEnd(scheduleIndex)}
                      >
                        <SortableContext
                          items={schedule.periods.map((_, i) => `period-${scheduleIndex}-${i}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2">
                            {schedule.periods.map((period, periodIndex) => {
                              const duration = calculateDuration(period.startTime, period.endTime);
                              const canConfigurePeriodTypes = !!(school?.periodTypes && school.periodTypes.length > 0);
                              
                              return (
                                <SortablePeriod
                                  key={`period-${scheduleIndex}-${periodIndex}`}
                                  id={`period-${scheduleIndex}-${periodIndex}`}
                                  period={period}
                                  periodIndex={periodIndex}
                                  scheduleIndex={scheduleIndex}
                                  duration={duration}
                                  canConfigurePeriodTypes={canConfigurePeriodTypes}
                                  schoolPrimaryColor={schoolPrimaryColor}
                                />
                              );
                            })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>

                  {school?.lunchSettings?.type === 'waves' && (
                    <div className="space-y-2 pt-3 border-t">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Lunch Waves</Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => addLunch(scheduleIndex)}
                          className="h-8 text-xs group transition-all hover:shadow-md"
                          style={{ 
                            borderColor: schoolPrimaryColor, 
                            color: schoolPrimaryColor,
                            backgroundColor: `${schoolPrimaryColor}08`
                          }}
                        >
                          <Plus className="h-3 w-3 mr-1 transition-transform group-hover:rotate-90" />
                          Add Wave
                        </Button>
                      </div>
                      
                      {schedule.lunches.length === 0 ? (
                        <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
                          <div className="text-2xl mb-2">🍔</div>
                          <p className="text-xs">No lunch waves</p>
                        </div>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleLunchDragEnd(scheduleIndex)}
                        >
                          <SortableContext
                            items={schedule.lunches.map((_, i) => `lunch-${scheduleIndex}-${i}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2">
                              {schedule.lunches.map((lunch, lunchIndex) => {
                                const duration = calculateDuration(lunch.startTime, lunch.endTime);
                                return (
                                  <SortableLunch
                                    key={`lunch-${scheduleIndex}-${lunchIndex}`}
                                    id={`lunch-${scheduleIndex}-${lunchIndex}`}
                                    lunch={lunch}
                                    lunchIndex={lunchIndex}
                                    scheduleIndex={scheduleIndex}
                                    duration={duration}
                                    schoolPrimaryColor={schoolPrimaryColor}
                                  />
                                );
                              })}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  )}
                    </CardContent>
                  </SortableSchedule>
                ))}
              </SortableContext>
            </DndContext>

            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={addSchedule}
                className="border-dashed"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Schedule
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="text-muted-foreground"
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload image
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);
